import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import {
  adminCashExchangeErrorsSelector,
  createAdminCashExchange,
  getAdminEditCashExchange,
  isLoadingCashExchangeSelector,
  selectedCashExchangeSelector,
  setAdminCashExchangeErrors,
  setSelectedCashExchange,
  updateAdminCashExchange,
} from 'src/store/adminCashExchange';

import {
  CustomButton,
  CustomInput,
  CustomSelect,
  LanguageSelect,
  PageLoader,
  WhiteCard,
} from 'src/components';

import './cashExchangesForm.scss';
import { APP_LANGUAGES } from 'src/constants';

export const CashExchangesForm = (props) => {
  const { t } = useTranslation('cashExchangesForm');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const params = useParams();

  const selectedCash = useSelector(selectedCashExchangeSelector);
  const isLoading = useSelector(isLoadingCashExchangeSelector);
  const formError = useSelector(adminCashExchangeErrorsSelector);

  const isEditMode = pathname?.includes('edit');

  const [formData, setFormData] = useState({
    active: true,
    operator: '',
    maps: '',
    descriptions: {
      2: {
        address: '',
        city: '',
        country: '',
      },
      1: {
        address: '',
        city: '',
        country: '',
      },
    },
  });
  const [language, setLanguage] = useState(1);

  const statusOptions = [
    {
      value: true,
      label: t('label:active'),
    },
    {
      value: false,
      label: t('label:inactive'),
    },
  ];

  useEffect(() => {
    if (isEditMode) {
      dispatch(getAdminEditCashExchange(params?.id));
    }

    return () => {
      dispatch(setSelectedCashExchange({}));
      dispatch(setAdminCashExchangeErrors({}));
    };
  }, [dispatch, isEditMode, params?.id]);

  useEffect(() => {
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, ...selectedCash }));
    }
  }, [isEditMode, selectedCash]);

  const onChangeInput = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.target.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminCashExchangeErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeLanguageInput = useCallback(
    (lng, key) => (event) => {
      setFormData((prev) => ({
        ...prev,
        descriptions: {
          ...prev.descriptions,
          [lng]: {
            ...prev.descriptions[lng],
            [key]: event.target.value,
          },
        },
      }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[`descriptions.${lng}.${key}`];

      dispatch(setAdminCashExchangeErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onChangeSelect = useCallback(
    (key) => (event) => {
      setFormData((prev) => ({ ...prev, [key]: event.value }));

      const copiedErrors = _.cloneDeep(formError);

      delete copiedErrors[key];

      dispatch(setAdminCashExchangeErrors(copiedErrors));
    },
    [dispatch, formError],
  );

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (isEditMode) {
        await dispatch(
          updateAdminCashExchange({ id: params?.id, payload: formData, navigate, setLanguage }),
        );
        return;
      }

      await dispatch(createAdminCashExchange({ navigate, payload: formData, setLanguage }));
    },
    [dispatch, navigate, formData, isEditMode, params?.id],
  );

  const selectedLanguage = APP_LANGUAGES?.find((item) => item.id === language);

  return (
    <div className="cash-exchanges-form">
      <WhiteCard>
        {isLoading ? <PageLoader /> : null}

        <h3>
          {t(`${!isEditMode ? 'create' : 'edit'}`)}
          <LanguageSelect language={selectedLanguage?.id} setLanguage={setLanguage} />
        </h3>
        <form onSubmit={onSubmit}>
          <div className="cash-exchanges-form__content">
            <CustomInput
              label={`${t('country')} (${selectedLanguage?.language?.toUpperCase()})`}
              value={formData.descriptions[language]?.country}
              errorMessage={formError?.[`descriptions.${language}.country`]}
              onChange={onChangeLanguageInput(language, 'country')}
            />
            <CustomInput
              label={`${t('city')} (${selectedLanguage?.language?.toUpperCase()})`}
              value={formData.descriptions[language]?.city}
              errorMessage={formError?.[`descriptions.${language}.city`]}
              onChange={onChangeLanguageInput(language, 'city')}
            />
            <CustomInput
              label={`${t('address')} (${selectedLanguage?.language?.toUpperCase()})`}
              value={formData.descriptions[language]?.address}
              errorMessage={formError?.[`descriptions.${language}.address`]}
              onChange={onChangeLanguageInput(language, 'address')}
            />

            <CustomInput
              label={t('gLink')}
              value={formData?.maps}
              errorMessage={formError?.maps}
              onChange={onChangeInput('maps')}
            />
            <CustomInput
              label={t('operator')}
              value={formData?.operator}
              errorMessage={formError?.operator}
              onChange={onChangeInput('operator')}
            />
            <CustomSelect
              label={t('status')}
              value={formData.active}
              options={statusOptions}
              onChange={onChangeSelect('active')}
            />
          </div>
          <CustomButton variant="filled" title={t('title:save')} />
        </form>
      </WhiteCard>
    </div>
  );
};
